import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/Dialog';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { useInputTextValidator } from '@/routes/_embaUserLayout/-hooks/useInputTextValidator';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type React from 'react';
import { useEffect } from 'react';
import { Explanation } from '../Explanation';
import styles from './index.module.scss';

export const COURSE_LESSON_PRACTICE_FREE_INPUT_QUESTION =
  graphql(/* GraphQL */ `
  fragment EmbaCourseLessonPracticeFreeInputQuestion on PracticeFreeInputQuestion {
    id
    content
    attachmentImageUrl
    explanationContent
    explanationAttachmentImageUrl
    userAnswer {
      id
      content
    }
  }
`);

export type FreeInputQuestionProps = {
  question: FragmentType<typeof COURSE_LESSON_PRACTICE_FREE_INPUT_QUESTION>;
  isAlreadyAnswered: boolean;
  answer: string;
  setAnswer: (value: string) => void;
  handleAnswerPracticeFreeInputQuestion: () => void;
  handleNext: () => void;
  isLastQuestion: boolean;
  allAnswered: boolean;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
  scrollToExplanation: () => void;
  answerPracticeFreeInputQuestionLoading: boolean;
};

export const FreeInputQuestion: React.FC<FreeInputQuestionProps> = ({
  question,
  isAlreadyAnswered,
  answer,
  setAnswer,
  handleAnswerPracticeFreeInputQuestion,
  handleNext,
  isLastQuestion,
  allAnswered,
  isAnsweredModalOpen,
  closeAnsweredModal,
  scrollToExplanation,
  answerPracticeFreeInputQuestionLoading,
}) => {
  const questionData = useFragment(
    COURSE_LESSON_PRACTICE_FREE_INPUT_QUESTION,
    question,
  );

  const { valid, isValid } = useInputTextValidator(answer);
  useEffect(() => {
    valid();
  }, [valid]);

  return (
    <>
      <textarea
        value={questionData.userAnswer?.content ?? answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={isAlreadyAnswered}
        className={styles.freeInputQuestionForm}
        maxLength={1000}
      />
      <div className={styles.freeInputQuestionFormCount}>
        <span>
          本設問の回答は受講管理者に共有されます。送信後の修正はできません。
        </span>
        {!isAlreadyAnswered && <span>(1000文字以内)</span>}
      </div>

      {isAlreadyAnswered && (
        <Explanation
          explanationContent={questionData.explanationContent}
          explanationAttachmentImageUrl={
            questionData.explanationAttachmentImageUrl
          }
        />
      )}

      <div className={styles.answerButton}>
        {!isAlreadyAnswered ? (
          <Button
            onClick={() => {
              handleAnswerPracticeFreeInputQuestion();
            }}
            disabled={!isValid}
            // Loadingが終わってからanswer取得するまでに時間がかかるため、isAnsweredModalOpenも利用
            loading={
              answerPracticeFreeInputQuestionLoading || isAnsweredModalOpen
            }
          >
            決定
          </Button>
        ) : (
          <Button
            onClick={() => {
              handleNext();
            }}
          >
            {isLastQuestion && allAnswered ? '終了' : '次の問題へ'}
          </Button>
        )}
      </div>
      <Dialog
        open={isAnsweredModalOpen && isAlreadyAnswered}
        onOpenChange={closeAnsweredModal}
      >
        <VisuallyHidden>
          <DialogDescription>演習問題解答結果のモーダル</DialogDescription>
        </VisuallyHidden>

        <DialogContent>
          <div className={styles.AnsweredDialog}>
            {questionData.userAnswer && (
              <>
                <DialogTitle className={styles.AnsweredDialogTitle}>
                  回答完了しました！
                </DialogTitle>

                <Button
                  onClick={() => {
                    scrollToExplanation();
                  }}
                >
                  解説を見る
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
